import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSignal } from "@preact/signals-react";
import PresentationModal from "../../../organisms/PresentationModal";
import { removeErrorField } from "../../../../utils/formErrorUtils";
import { ErrorField } from "../../../../types/ErrorField";
import Select, { ActionMeta, OnChangeValue, StylesConfig } from 'react-select';

import { FaArrowRight } from "react-icons/fa";
import NewTooltip from "../../../atoms/NewTooltip";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import TextEdit from "../../../molecules/TextEdit";
import { ProfileStorageManager } from "../../../../utils/profileStorageManager";

type Form = {
    profiles: string[];
}

const BASE_URL = process.env.REACT_APP_PERSON_API_URL;
const API_KEY = process.env.REACT_APP_API_KEY || '';

const SelectProfilesIndividualModal: React.FC = () => {
    const navigate = useNavigate();
    const isSendingForm = useSignal<boolean>(false);
    const errorFields = useSignal<ErrorField[]>([]);

    const [params, setParams] = useSearchParams();
    const profiles = params.get('profiles');
    const socialId = params.get('socialId');
    const name = params.get('name');

    const { personId = "" } = useParams();
    
    // 🔧 FIX: Garantir que personId não seja vazio
    const validPersonId = personId || 'temp-profile-id';
    
    // ✅ CORREÇÃO: Mover log para useEffect para evitar spam
    useEffect(() => {
        console.log('🔍 Modal personId:', personId, '→ validPersonId:', validPersonId);
    }, [personId, validPersonId]);

    const initialProfiles = profiles ? profiles?.split(',') : [];

    const [profileOptions, setProfileOptions] = useState<{ value: string; label: string }[]>([]);
    const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);

    // Definir PROFILES e orderOptions
    const PROFILES = [
        { value: 'SIGNATORY', label: 'Signatário' },
        { value: 'CONSENTING_PARTY', label: 'Anuente' },
        { value: 'GUARANTOR', label: 'Garantidor' }
    ];

    const orderOptions = (values: readonly { value: string; label: string; isFixed?: boolean }[]) => {
        return values
            .filter((v) => v.isFixed)
            .concat(values.filter((v) => !v.isFixed));
    };

    const styles: StylesConfig<{ value: string; label: string; isFixed?: boolean }, true> = {
        multiValue: (base, state) => {
            return state.data.isFixed ? { ...base, backgroundColor: 'gray' } : base;
        },
        multiValueLabel: (base, state) => {
            return state.data.isFixed
                ? { ...base, fontWeight: 'bold', color: 'white', paddingRight: 6 }
                : base;
        },
        multiValueRemove: (base, state) => {
            return state.data.isFixed ? { ...base, display: 'none' } : base;
        },
        menu: (base) => ({
            ...base,
            zIndex: 9999, // Z-index alto para sair do modal
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999, // Z-index alto para portal
        }),
    };

    const form = useSignal<Form>({
        profiles: initialProfiles,
    });

    const fixedProfiles = initialProfiles.map(profile => ({
        value: profile,
        label: PROFILES.find((p: any) => p.value === profile)?.label || profile,
        isFixed: true,
    }));

    const [selectedOptions, setSelectedOptions] = useState<readonly { value: string; label: string; isFixed?: boolean }[]>(orderOptions(fixedProfiles));

    // Carregar perfis da API
    useEffect(() => {
        const loadProfiles = async () => {
            setIsLoadingProfiles(true);
            try {
                const token = localStorage.getItem('authToken');
                const response = await fetch(`${BASE_URL}/individual/subscription-profiles`, {
                    method: 'GET',
                    headers: {
                        'x-api-key': API_KEY || '',
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    const profiles = data.Profiles || [];
                    
                    // Transformar dados da API em opções do Select
                    const options = profiles.map((profile: any) => ({
                        value: profile.profile,
                        label: profile.label
                    }));
                    
                    setProfileOptions(options);
                } else {
                    console.error('Erro ao carregar perfis:', response.status);
                    // Fallback para perfis hardcoded
                    setProfileOptions(PROFILES);
                }
            } catch (error) {
                console.error('Erro ao carregar perfis:', error);
                // Fallback para perfis hardcoded
                setProfileOptions(PROFILES);
            } finally {
                setIsLoadingProfiles(false);
            }
        };

        loadProfiles();
    }, []);

    const changeForm = (field: string, value: any) => {
        form.value = { ...form.value, [field]: value };
        removeErrorField(errorFields.value, field);
    };

    const submitForm = async () => {
        if (form.value.profiles.length === 0) {
            toast.error("Selecione ao menos um perfil!");
            return;
        }
        if (form.value.profiles.length > 0 && form.value.profiles.length == initialProfiles.length) {
            toast.error("Selecione um perfil diferente do perfil atual!");
            return;
        }
        
        isSendingForm.value = true;
        
        // 1. Salvar em localStorage ANTES de navegar
        const success = ProfileStorageManager.save('individual', validPersonId, form.value.profiles);
        
        if (success) {
            // 2. Se localStorage funcionou, navegar SEM query string
            console.log('✅ Navegando sem query string (localStorage salvo)');
            navigate(`/person/individual/edit/${validPersonId}?tab=0`, {
                state: { fromProfileModal: true }
            });
        } else {
            // 3. Fallback: mesmo sem localStorage, navegar sem query string
            console.log('⚠️ Fallback: navegando sem query string');
            navigate(`/person/individual/edit/${validPersonId}?tab=0`, {
                state: { fromProfileModal: true }
            });
        }

        // try {
        //     const token = localStorage.getItem('authToken');
        //     let sendForm: Form = {
        //         profiles: form.value.profiles,
        //     };


        //     const response = await fetch(`${BASE_URL}`, {
        //         method: 'POST',
        //         headers: {
        //             'x-api-key': API_KEY || '',
        //             'Content-Type': 'application/json',
        //             'Authorization': `Bearer ${token}`,
        //         },
        //         body: JSON.stringify(sendForm),
        //     });

        //     if (response.ok) {
        //         navigate(`/person/individual/edit/${personId}?tab=0&profiles=${form.value.profiles.join(',')}`);
        //     } else if (response.status === 401) {
        //         toast.error('A sessão expirou. Faça login novamente.');
        //         navigate('/login');
        //     } else if (response.status === 409) {
        //         const { errors, message } = await response.json();
        //         toast.error(message);
        //         errorFields.value = errors ? errors.map((error: any) => ({ fieldName: error.field, message: error.message })) : [];
        //     } else {
        //         toast.error("Ocorreu um erro na comunicação com o servidor!\nTente novamente mais tarde.");
        //     }
        // } catch (error) {
        //     toast.error("Ocorreu um erro na comunicação com o servidor!\nTente novamente mais tarde.");
        // }
        isSendingForm.value = false;
    };

    const onChange = (
        newValue: OnChangeValue<{ value: string; label: string; isFixed?: boolean }, true>,
        actionMeta: ActionMeta<{ value: string; label: string; isFixed?: boolean }>,
    ) => {
        switch (actionMeta.action) {
            case 'remove-value':
            case 'pop-value':
                if (actionMeta.removedValue.isFixed) {
                    return;
                }
                break;
            case 'clear':
                newValue = fixedProfiles;
                break;
        }

        const orderedValues = orderOptions(newValue);
        setSelectedOptions(orderedValues);
        changeForm('profiles', orderedValues.map(v => v.value));
    };

    const disableForward = () => {
        return selectedOptions.length === 0 || selectedOptions.length === initialProfiles.length;
    };

    return (
        <PresentationModal
            id="select-profiles-modal"
            show={true}
            onClose={() => navigate(-1)}
            header="Adicionar novo perfil"
        >
            <div className="p-4">
                <div className="mb-4 h-64">

                    <div className="grid grid-cols-4 gap-4 mb-4">
                        <TextEdit disabled={true}
                            id="govTaxId"
                            label="CPF"
                            className="col-span-1"
                            value={socialId || ""}
                        />
                        <TextEdit disabled={true} id="partner"
                            label="Nome"
                            className="col-span-3"
                            value={name || ""}
                        />
                    </div>

                    <label htmlFor="profiles">Perfis:</label>
                    <Select
                        id="select-profiles"
                        inputId="profiles"
                        name="profiles"
                        isMulti={true}
                        closeMenuOnSelect={false}
                        isClearable={selectedOptions.some((v) => !v.isFixed)}
                        options={profileOptions}
                        isLoading={isLoadingProfiles}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        onChange={onChange}
                        placeholder="Selecione o(s) perfil(is) desejado(s)"
                        value={selectedOptions}
                        styles={styles}
                        menuPortalTarget={document.body}
                    />
                </div>
                <div className="flex justify-end">
                    <NewTooltip content={<div className="w-48">Cancelar a edição e voltar para a tela anterior</div>}>
                        <button
                            id="btn-cancel-profiles"
                            type="button"
                            onClick={() => navigate(-1)}
                            className="mr-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                        >
                            Cancelar
                        </button>
                    </NewTooltip>

                    <NewTooltip content={<div className="w-24">Avançar para a próxima etapa</div>}>
                        <button
                            id="btn-advance-profiles"
                            type="button"
                            onClick={submitForm}
                            disabled={disableForward()}
                            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                            Avançar
                            <FaArrowRight className="ml-2" />
                        </button>
                    </NewTooltip>
                </div>

            </div>
        </PresentationModal>
    );
};

export default SelectProfilesIndividualModal;